import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api, ApiError, formatDate, scr } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { M12_ROLES, hasAnyRole } from '../../lib/roles';
import type { ImportExportPermit } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { ErrorNote, PageHeader, Spinner, StatusBadge } from '../../ui/components';

export function PermitDetail() {
  const { ref = '' } = useParams();
  const { persona } = useAuth();
  const isOfficer = hasAnyRole(persona?.roles, M12_ROLES.TRADE_OFFICER);
  const permitQ = useApiQuery<ImportExportPermit>(`/api/v1/m12/import-export-permits/${ref}`);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [revokeReason, setRevokeReason] = useState('');

  if (permitQ.loading) return <Spinner label="Loading permit…" />;
  if (permitQ.error || !permitQ.data) {
    return (
      <div>
        <Link to="/trade/permits" className="text-sm font-semibold text-brand-600 hover:underline">← Back to permits</Link>
        <div className="mt-4"><ErrorNote message={permitQ.error ?? 'Permit not found'} /></div>
      </div>
    );
  }
  const permit = permitQ.data;

  const decideReferral = async (authorityCode: string, decision: 'CLEARED' | 'REJECTED') => {
    setBusy(authorityCode);
    setError(null);
    try {
      await api.post(`/api/v1/m12/import-export-permits/${ref}/referrals/${authorityCode}/decide`, { status: decision });
      permitQ.reload();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Decision failed');
    } finally {
      setBusy(null);
    }
  };

  const revoke = async () => {
    setBusy('revoke');
    setError(null);
    try {
      await api.post(`/api/v1/m12/import-export-permits/${ref}/revoke`, { reason: revokeReason });
      permitQ.reload();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Revoke failed');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div>
      <Link to="/trade/permits" className="mb-3 inline-block text-sm font-semibold text-brand-600 hover:underline">← Back to permits</Link>
      <PageHeader title={`Permit ${permit.refNo}`} subtitle={`${permit.direction} · ${permit.applicantType} ${permit.applicantId}`} actions={<StatusBadge status={permit.state} />} />

      {error ? <div className="mb-4"><ErrorNote message={error} /></div> : null}

      <div className="card mb-5 p-5">
        <dl className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-3">
          <div><dt className="text-xs uppercase text-slate-400">HS codes</dt><dd>{permit.hsCodes.join(', ')}</dd></div>
          <div><dt className="text-xs uppercase text-slate-400">Route</dt><dd>{permit.originCountry} → {permit.destinationCountry}</dd></div>
          <div><dt className="text-xs uppercase text-slate-400">Declared value</dt><dd>{permit.declaredValue} {permit.currency}</dd></div>
          <div><dt className="text-xs uppercase text-slate-400">Valid</dt><dd>{permit.validFrom ? `${formatDate(permit.validFrom)} – ${formatDate(permit.validTo)}` : '—'}</dd></div>
        </dl>
        <p className="mt-3 text-sm text-slate-700">{permit.goodsDescription}</p>
        {permit.rejectionReason ? <div className="mt-2"><ErrorNote message={permit.rejectionReason} /></div> : null}
        {permit.revokedReason ? <div className="mt-2"><ErrorNote message={`Revoked: ${permit.revokedReason}`} /></div> : null}
      </div>

      <div className="card p-5">
        <h2 className="mb-3 text-sm font-bold text-slate-700">Competent-authority referrals</h2>
        {permit.referrals.length === 0 ? (
          <p className="text-xs text-slate-400">No referral required for these HS codes.</p>
        ) : (
          <ul className="space-y-2">
            {permit.referrals.map((r) => (
              <li key={r.id} className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2 text-sm">
                <span>{r.authorityName} <span className="text-xs text-slate-400">({r.authorityCode})</span></span>
                <div className="flex items-center gap-2">
                  <StatusBadge status={r.status} />
                  {isOfficer && ['PENDING_DISPATCH', 'REFERRED'].includes(r.status) ? (
                    <>
                      <button className="btn-success !px-2 !py-0.5 text-[11px]" onClick={() => decideReferral(r.authorityCode, 'CLEARED')} disabled={busy !== null}>Clear</button>
                      <button className="btn-danger !px-2 !py-0.5 text-[11px]" onClick={() => decideReferral(r.authorityCode, 'REJECTED')} disabled={busy !== null}>Reject</button>
                    </>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {isOfficer && permit.state === 'ISSUED' ? (
        <div className="card mt-5 p-5">
          <h2 className="mb-2 text-sm font-bold text-slate-700">Revoke this permit</h2>
          <div className="flex gap-2">
            <input className="field-input" placeholder="Reason…" value={revokeReason} onChange={(e) => setRevokeReason(e.target.value)} />
            <button className="btn-danger shrink-0" onClick={revoke} disabled={busy !== null || revokeReason.length < 3}>{busy === 'revoke' ? 'Revoking…' : 'Revoke'}</button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
